<template>
  <div v-if="domainLike" class="vds-domain-check" role="status" aria-live="polite">
    <div v-if="checking" class="text-caption d-flex align-center vds-domain-check__checking">
      <v-progress-circular indeterminate size="13" width="2" class="mr-2" aria-hidden="true" />
      Checking DNS…
    </div>

    <template v-else-if="result">
      <!-- Backend couldn't parse the input as a domain. -->
      <v-alert v-if="result.domain == null" type="warning" dense text class="mb-0 py-2 kvm-dc-alert kvm-dc-alert--warn">
        That doesn't look like a valid domain.
      </v-alert>

      <!-- Points at the VPS — good for SSL. -->
      <v-alert v-else-if="result.matches" type="success" dense text class="mb-0 py-2 kvm-dc-alert kvm-dc-alert--ok">
        <strong>{{ result.domain }}</strong> points to your server ({{ result.target_ip }}).
      </v-alert>

      <!-- Doesn't point at the VPS — warn about SSL. -->
      <v-alert v-else type="warning" dense text class="mb-0 py-2 kvm-dc-alert kvm-dc-alert--warn">
        <strong>{{ result.domain }}</strong> doesn't point to your server's IP
        (<strong>{{ result.target_ip }}</strong>){{ resolvedText }}.
        SSL (Let's Encrypt) won't be able to issue until its DNS points here — you can still deploy and fix DNS later.
      </v-alert>
    </template>
  </div>
</template>

<script>
import kvm from '@/api/kvm'

const DEBOUNCE_MS = 600
// Only check once the input looks like a complete domain (avoids churn mid-typing).
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i

export default {
  name: 'KvmDomainCheck',
  props: {
    entitlementId: { type: [String, Number], default: null },
    domain: { type: String, default: '' },
  },
  data: () => ({
    checking: false,
    result: null,
    timer: null,
    reqId: 0,
  }),
  computed: {
    trimmed () {
      return (this.domain || '').trim()
    },
    domainLike () {
      return DOMAIN_RE.test(this.trimmed)
    },
    resolvedText () {
      const r = (this.result && this.result.resolved) || []
      return r.length ? ` — it currently resolves to ${r.join(', ')}` : ' — it doesn’t resolve yet'
    },
  },
  watch: {
    domain () { this.schedule() },
    entitlementId () { this.schedule() },
  },
  beforeDestroy () {
    if (this.timer) clearTimeout(this.timer)
  },
  methods: {
    schedule () {
      if (this.timer) clearTimeout(this.timer)
      this.result = null
      if (!this.domainLike || !this.entitlementId) {
        this.checking = false
        return
      }
      this.timer = setTimeout(this.run, DEBOUNCE_MS)
    },
    async run () {
      const id = ++this.reqId
      const domain = this.trimmed
      try {
        this.checking = true
        const res = await kvm.checkDns(this.entitlementId, domain)
        if (id === this.reqId) this.result = res // ignore superseded responses
      } catch (e) {
        if (id === this.reqId) this.result = null // stay quiet on transient errors
      } finally {
        if (id === this.reqId) this.checking = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-domain-check__checking {
  @include kvm-secondary-text;
}

// Vuetify's `text` alert variant colours its body with the (low-contrast) theme
// colour; force the message text to a foreground tone that clears AA on the faint
// tinted backgrounds, in both themes. Status meaning is also carried by the words
// (not colour alone).
.kvm-dc-alert ::v-deep .v-alert__content {
  color: rgba(0, 0, 0, 0.87); // on the pale success/warning tint ≈ 13:1+ (AA)
}
.theme--dark .kvm-dc-alert ::v-deep .v-alert__content {
  color: rgba(255, 255, 255, 0.92);
}

// Keep a semantic icon but nudge it to >=3:1 (non-text contrast) on each tint.
.kvm-dc-alert--ok ::v-deep .v-alert__icon.v-icon {
  color: #0a7c3c !important; // green on pale-green tint ≈ 5:1 (light)
}
.theme--dark .kvm-dc-alert--ok ::v-deep .v-alert__icon.v-icon {
  color: #3db14e !important;
}
.kvm-dc-alert--warn ::v-deep .v-alert__icon.v-icon {
  color: #8a5a00 !important; // dark amber on pale-amber tint ≈ 5:1 (light)
}
.theme--dark .kvm-dc-alert--warn ::v-deep .v-alert__icon.v-icon {
  color: #ffb74d !important; // light amber on dark tint ≈ 6:1+ (dark)
}
</style>
